import React from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { Button } from "main/javascripts/components/button/Button";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import {
  fontSize,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { GuestPickerInputRoom } from "./GuestPickerInputRoom";
import {
  DEFAULT_ADULTS as HOTEL_DEFAULT_ADULTS,
  MAX_NUMBER_OF_ROOMS,
} from "main/javascripts/constants/HotelConstants";
import {
  DEFAULT_ADULTS as CRUISE_DEFAULT_ADULTS,
  MAX_NUMBER_OF_CABINS,
} from "main/javascripts/constants/CruiseConstants";
import { accentColor } from "main/javascripts/styles/base/colorStyle";
import { FormNamespaces } from "../../../constants/FormConstants";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

interface IProps {
  namespace: string;
  groupName?: string;
  onUpdate?(): void;
}

// バリデーションやエラー表示をやりやすくするために
// react hook formに依存している
export const GuestPicker: React.FC<IProps> = (
  props: IProps
): React.ReactElement<IProps> => {
  const { namespace, groupName = "guests", onUpdate } = props;
  const { t } = useTranslation(["button", "component"]);

  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: groupName,
  });
  const values = useWatch({ control, name: groupName });

  const defaultRoomValues = {
    number_of_adult:
      namespace === FormNamespaces.cruise
        ? CRUISE_DEFAULT_ADULTS
        : HOTEL_DEFAULT_ADULTS,
    number_of_children: 0,
  };

  const onPickerUpdate = (): void => {
    if (onUpdate) {
      onUpdate();
    }
  };

  const onRoomAdd = (e) => {
    e.stopPropagation();
    append(defaultRoomValues);
    if (onUpdate) {
      onUpdate();
    }
  };

  const onRoomDelete = (e) => {
    e.stopPropagation();
    if (values.length > 1) {
      remove(values.length - 1);
      if (onUpdate) {
        onUpdate();
      }
    }
  };

  const addRoomButtonText =
    namespace === FormNamespaces.cruise
      ? t("button:cruise.addCabin")
      : t("button:hotel.addRoom");

  const removeRoomButtonText =
    namespace === FormNamespaces.cruise
      ? t("button:cruise.removeCabin")
      : t("button:hotel.removeRoom");

  const max_number_of_rooms =
    namespace === FormNamespaces.cruise
      ? MAX_NUMBER_OF_CABINS
      : MAX_NUMBER_OF_ROOMS;

  return (
    <div css={blockStyle}>
      <div>
        <div>
          {fields.map((field, index) => {
            const roomGroupName = `${groupName}.${index}`;
            return (
              <GuestPickerInputRoom
                key={field.id}
                namespace={namespace}
                groupName={roomGroupName}
                index={index}
                onUpdate={onPickerUpdate}
              />
            );
          })}
          <div css={roomButtonBlockStyle}>
            {values.length > 1 && (
              <div css={removeButtonStyle}>
                <Button
                  type="button"
                  onClick={onRoomDelete}
                  styleKey="buttonSmallAlertSecondary"
                >
                  <div css={roomButtonInner}>
                    <div css={removeIconStyle} />
                    {removeRoomButtonText}
                  </div>
                </Button>
              </div>
            )}
            {values.length < max_number_of_rooms && (
              <div css={addButtonStyle}>
                <Button
                  type="button"
                  onClick={onRoomAdd}
                  styleKey="buttonSmallSecondary"
                >
                  <div css={roomButtonInner}>
                    {addRoomButtonText}
                    <div css={addIconStyle} />
                  </div>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const blockStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  width: auto;
  background-color: white;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  padding: ${space.atom2x} 0 0;
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.caption};
    line-height: ${lineHeight.caption3};
  }
`;
const roomButtonBlockStyle = css`
  padding: ${space.atom2x} ${space.atom2x} ${space.atom2x};
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 24rem;
  margin: 0 auto;
  ${moreThanBreakpoint("tablet")} {
    padding-top: ${space.atom};
  }
`;
const roomButtonInner = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const removeButtonStyle = css`
  width: 46%;
  margin: 0 2%;
  color: ${accentColor.alertColor};
  display: flex;
  justify-content: center;
`;
const removeIconStyle = css`
  position: relative;
  width: 1rem;
  height: 1rem;
  margin-right: ${space.atom0_5x};
  margin-left: -${space.atom};
  &:before {
    content: "";
    display: block;
    width: 60%;
    height: 0.125rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${accentColor.alertColor};
  }
`;
const addButtonStyle = css`
  width: 46%;
  margin: 0 2%;
  color: ${accentColor.primaryColor};
  display: flex;
  justify-content: center;
`;
const addIconStyle = css`
  position: relative;
  width: 1rem;
  height: 1rem;
  margin-left: ${space.atom0_5x};
  margin-right: -${space.atom};
  &:before {
    content: "";
    display: block;
    width: 60%;
    height: 0.125rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${accentColor.primaryColor};
  }
  &:after {
    content: "";
    display: block;
    width: 0.125rem;
    height: 60%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${accentColor.primaryColor};
  }
`;
